import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import ActionButton from "../../Button/ActionButton";
import useProfessionalProfile from "../../../hooks/useProfessionalProfile";
import { serverMessage } from "../../../services/professionals";
import { outlinedFieldSx } from "../../../utils/fieldStyles";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

// Adds an email to the professional's list.
//
// The address is stored trimmed and lower-cased, and one the professional
// already lists is rejected case-insensitively. The local check below catches
// the obvious cases so the round trip is spared; the server stays the authority,
// and its message is what gets shown when it refuses.

/** Deliberately loose: the server, not this, decides what a valid address is. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  open: boolean;
  /** Addresses already on file, to catch a duplicate before sending it. */
  existing: string[];
  onClose: () => void;
  onAdded: () => void;
}

export default function AddEmailDialog({
  open,
  existing,
  onClose,
  onAdded
}: Props) {
  const { t } = useTranslation();
  const { addEmail } = useProfessionalProfile();

  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue("");
    setError(null);
  }, [open]);

  const trimmed = value.trim().toLowerCase();
  const malformed = trimmed.length > 0 && !EMAIL_PATTERN.test(trimmed);
  const duplicate = existing.some(entry => entry.toLowerCase() === trimmed);

  const fieldError = malformed
    ? t("contactLists.emailInvalid")
    : duplicate
    ? t("contactLists.emailDuplicate")
    : undefined;

  const canSubmit = trimmed.length > 0 && !fieldError && !saving;

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await addEmail(trimmed);
      onAdded();
      onClose();
    } catch (err) {
      setError(serverMessage(err) ?? t("contactLists.emailAddError"));
    } finally {
      setSaving(false);
    }
  }, [canSubmit, trimmed, addEmail, onAdded, onClose, t]);

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={t("contactLists.addEmailTitle")}
      maxWidth="sm"
    >
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          type="email"
          label={t("contactLists.emailLabel")}
          value={value}
          onChange={event => setValue(event.target.value)}
          error={Boolean(fieldError)}
          helperText={fieldError ?? ""}
          inputProps={{ inputMode: "email" }}
          sx={outlinedFieldSx}
        />
        <Typography
          sx={{
            ...typography.body.s,
            color: semanticColors.text.secondary,
            mt: 1,
            mb: 2
          }}
        >
          {t("contactLists.emailNote")}
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Box display="flex" gap={1} p={1}>
          <ActionButton
            label={t("common.cancel")}
            onClick={onClose}
            variant="secondary"
          />
          <ActionButton
            label={t("contactLists.add")}
            onClick={onSubmit}
            loading={saving}
            disabled={!canSubmit}
          />
        </Box>
      </DialogActions>
    </DialogComponent>
  );
}
